import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sanitizeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const full = trimmed.match(/^https?:\/\//i) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(full);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function sanitizeDisplayText(text: string, maxLength = 500): string {
  return text.replace(/<[^>]*>/g, '').trim().slice(0, maxLength);
}

export function getYouTubeInfo(url: string | null | undefined): { 
  videoId: string | null; 
  embedUrl: string | null; 
  watchUrl: string | null; 
  isValid: boolean 
} {
  if (!url) return { videoId: null, embedUrl: null, watchUrl: null, isValid: false };

  try {
    // Add protocol if missing for URL parsing
    const safeUrl = url.match(/^https?:\/\//i) ? url : `https://${url}`;
    const parsed = new URL(safeUrl);
    const hostname = parsed.hostname.toLowerCase();
    const pathname = parsed.pathname;
    let videoId: string | null = null;

    if (hostname.includes('youtu.be')) {
      // https://youtu.be/VIDEO_ID
      videoId = pathname.slice(1);
    } else if (hostname.includes('youtube.com')) {
      if (pathname.includes('/watch')) {
        // https://www.youtube.com/watch?v=VIDEO_ID
        videoId = parsed.searchParams.get('v');
      } else if (pathname.includes('/embed/')) {
        // https://www.youtube.com/embed/VIDEO_ID
        videoId = pathname.split('/embed/')[1];
      } else if (pathname.includes('/shorts/')) {
        // https://www.youtube.com/shorts/VIDEO_ID
        videoId = pathname.split('/shorts/')[1];
      } else if (pathname.startsWith('/v/')) {
         // Legacy: https://www.youtube.com/v/VIDEO_ID
         videoId = pathname.split('/v/')[1];
      }
    }

    // Clean video ID (remove extra params like ?t= or &si= if they stuck to the ID)
    if (videoId) {
      videoId = videoId.split(/[?#&]/)[0];
    }

    // Validate ID format (11 chars, alphanumeric + _ -)
    if (videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
      return {
        videoId,
        embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`,
        watchUrl: `https://www.youtube.com/watch?v=${videoId}`,
        isValid: true
      };
    }
  } catch (e) {
    // Invalid URL format
  }

  return { videoId: null, embedUrl: null, watchUrl: null, isValid: false };
}